import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Trash2, Check } from "lucide-react";
import AvatarImage from "./AvatarImage";

export interface MultiSelectOption {
  id: string; // stable id
  label: string; // display label
  // Optional avatar support
  avatarName?: string;
  avatarFilename?: string | null;
}

export interface MultiSelectProps {
  label?: string;
  options: MultiSelectOption[];
  selectedIds: string[]; // store ids to avoid rename issues
  onSelectionChange: (ids: string[]) => void;
  placeholder?: string;
  width?: string;
  dropdownWidth?: string;
  disabled?: boolean;
  showAvatars?: boolean; // when true and avatar data is available
}

export default function MultiSelect({
  label = "Select",
  options,
  selectedIds,
  onSelectionChange,
  placeholder = "Select options",
  width = "w-full",
  dropdownWidth = "w-full",
  disabled = false,
  showAvatars = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return options;
    return options.filter(o => o.label.toLowerCase().includes(s));
  }, [search, options]);

  const toggle = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(n => n !== id)
      : [...selectedIds, id];
    onSelectionChange(next);
  };

  return (
    <div className="flex flex-col gap-1 relative" ref={ref}>
      <label className="text-xs text-gray-500">{label}</label>
      <div
        onClick={() => !disabled && setOpen(o => !o)}
        className={`${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'} bg-white text-gray-900 min-h-[40px] ${width} rounded text-sm flex items-center gap-1 flex-wrap border border-gray-300 px-2 py-1`}
      >
        {selectedIds.length === 0 && (
          <span className="text-gray-500">{placeholder}</span>
        )}
        {selectedIds.map(id => {
          const it = options.find(o => o.id === id);
          const labelText = it?.label || id;
          return (
            <span key={id} className="text-[11px] px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700 mr-1 mb-1">
              {labelText}
              <button onClick={(e) => { e.stopPropagation(); toggle(id); }} className="ml-1 text-gray-500 hover:text-gray-700">×</button>
            </span>
          );
        })}
        <ChevronDown className={`ml-auto h-4 w-4 text-gray-500 ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && (
        <div className={`absolute top-full left-0 mt-1 bg-white text-gray-900 rounded shadow-lg p-2 z-50 border border-gray-200 ${dropdownWidth}`}>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-2 py-1 mb-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="max-h-56 overflow-auto pr-1">
            {filtered.map(o => (
              <label key={o.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                {showAvatars ? (
                  <AvatarImage name={o.avatarName || o.label} filename={o.avatarFilename || null} size={20} />
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate font-medium text-left" title={o.label}>{o.label}</div>
                </div>
                <input
                  checked={selectedIds.includes(o.id)}
                  onChange={() => toggle(o.id)}
                  type="checkbox"
                  className="h-4 w-4 flex-shrink-0"
                />
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => onSelectionChange([])}
              className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
            >
              <Trash2 className="h-3.5 w-3.5"/>Clear
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-xs bg-gray-900 text-white px-2 py-1 rounded flex items-center gap-1"
            >
              <Check className="h-3.5 w-3.5"/>Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


